// 🔐 Connexion P4 — liste des appareils connectés (réutilisée : Mon espace, Comptes et accès, fiche employé).
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LogOut, Monitor, RefreshCw, Smartphone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

export function tempsRelatif(iso, maintenant = new Date()) {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((maintenant - new Date(iso)) / 1000));
  if (s < 60) return "à l'instant";
  const m = Math.round(s / 60); if (m < 60) return `il y a ${m} min`;
  const h = Math.round(m / 60); if (h < 24) return `il y a ${h} h`;
  const j = Math.round(h / 24); return `il y a ${j} j`;
}
const dateCourte = (iso) => (iso ? new Date(iso).toLocaleString('fr-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

/** userId absent → mes appareils ; userId présent → appareils d'un utilisateur (propriétaire). */
export function ListeAppareils({ userId = null, onChange }) {
  const [sessions, setSessions] = useState(null);
  const [confirmation, setConfirmation] = useState(null); // { sid } | { tous: true }
  const [enCours, setEnCours] = useState(false);
  const base = userId ? `${API_URL}/api/auth/users/${userId}/sessions` : `${API_URL}/api/auth/sessions`;

  const charger = useCallback(async () => {
    try { const { data } = await axios.get(base, { headers: headers() }); setSessions(data.sessions || []); onChange?.(data.sessions || []); }
    catch (e) { toast.error(e.response?.data?.detail || 'Impossible de charger les appareils'); setSessions([]); }
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [charger]);

  const executer = async () => {
    setEnCours(true);
    try {
      let url;
      if (confirmation.tous) url = userId ? `${base}/revoquer-toutes` : `${base}/revoquer-autres`;
      else url = `${base}/${confirmation.sid}/revoquer`;
      const { data } = await axios.post(url, {}, { headers: headers() });
      toast.success(confirmation.tous ? `${data.revoquees ?? ''} appareil(s) déconnecté(s)`.trim() : 'Appareil déconnecté');
      if (!userId && (data.courante || false)) { localStorage.removeItem('auth_token'); window.location.assign('/login'); return; }
      setConfirmation(null); await charger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la déconnexion'); }
    finally { setEnCours(false); }
  };

  if (sessions === null) return <p className="text-sm text-slate-500" data-testid="appareils-chargement">Chargement des appareils…</p>;
  return (
    <div className="space-y-2" data-testid="liste-appareils">
      {sessions.length === 0 && <p className="text-sm text-slate-500" data-testid="appareils-vide">Aucun appareil connecté</p>}
      {sessions.map((s) => (
        <div key={s.sid} className={`flex items-center gap-3 border rounded-lg p-3 ${s.courante ? 'border-blue-300 bg-blue-50/40' : 'bg-white'}`} data-testid={`appareil-${s.sid}`}>
          {/Mobile/.test(s.appareil || '') ? <Smartphone className="w-5 h-5 text-slate-500 shrink-0" /> : <Monitor className="w-5 h-5 text-slate-500 shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium flex items-center gap-2">{s.appareil || 'Appareil inconnu'}{s.courante && <Badge className="bg-blue-600 text-white text-[10px]" data-testid={`appareil-${s.sid}-courant`}>Cet appareil</Badge>}</p>
            <p className="text-xs text-slate-500">IP {s.ip} · dernière activité <span data-testid={`appareil-${s.sid}-activite`}>{tempsRelatif(s.derniere_activite)}</span> · connecté le {dateCourte(s.cree_a)}</p>
          </div>
          <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => setConfirmation({ sid: s.sid, courante: s.courante })} data-testid={`appareil-${s.sid}-deconnecter`}><LogOut className="w-3.5 h-3.5 mr-1" /> Déconnecter</Button>
        </div>
      ))}
      <div className="flex items-center justify-between pt-1">
        <Button size="sm" variant="ghost" onClick={charger} data-testid="appareils-rafraichir"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualiser</Button>
        {sessions.length > (userId ? 0 : 1) && (
          <Button size="sm" variant="outline" onClick={() => setConfirmation({ tous: true })} data-testid="appareils-deconnecter-tous">
            {userId ? 'Tout déconnecter' : 'Déconnecter tous les autres appareils'}
          </Button>
        )}
      </div>
      <Dialog open={!!confirmation} onOpenChange={(o) => { if (!o) setConfirmation(null); }}>
        <DialogContent className="max-w-md" data-testid="dialogue-confirmer-deconnexion">
          <DialogHeader>
            <DialogTitle>Déconnecter {confirmation?.tous ? (userId ? 'tous les appareils' : 'tous les autres appareils') : 'cet appareil'} ?</DialogTitle>
            <DialogDescription>{confirmation?.courante ? 'Il s\'agit de votre appareil actuel : vous serez redirigé vers la page de connexion.' : 'La session concernée sera terminée immédiatement ; une nouvelle connexion sera nécessaire.'}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmation(null)} disabled={enCours} data-testid="confirmer-deconnexion-annuler">Annuler</Button>
            <Button variant="destructive" onClick={executer} disabled={enCours} data-testid="confirmer-deconnexion-valider">Déconnecter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Dialogue propriétaire : appareils d'un utilisateur donné. */
export function DialogueAppareils({ utilisateur, open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="dialogue-appareils">
        <DialogHeader>
          <DialogTitle>Appareils connectés — {utilisateur?.prenom} {utilisateur?.nom}</DialogTitle>
          <DialogDescription>{utilisateur?.email}. Déconnecter un appareil termine sa session immédiatement.</DialogDescription>
        </DialogHeader>
        {utilisateur?.id && <ListeAppareils userId={utilisateur.id} />}
      </DialogContent>
    </Dialog>
  );
}
